import type { FlowRates } from "@/libs/calculations/productionRates";
import type {
  BasicData,
  FluidPropertiesOverrides
} from "@/libs/calculations/fluidProperties/d";
import type { Correlations } from "../../forms/formTypes";
import type {
  DeviationSurveyEntryRows,
  ScenarioCalculations
} from "../calculationTypes";
import type { Override_Options_Enum } from "gql/api";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import type {
  FluidPropertiesPipelineResults,
  IntakeInfo
} from "../fluidPropertiesPipelineTypes";
import type { FluidPropertiesPipelineInputs } from "./types";
import { calculateDatumAndPressureGradient } from "./stages/datumAndGradient";
import { calculatePerfsToBottomOfEquipment } from "./stages/perfsToBottomOfEquipment";
import { calculateSensorToMotor } from "./stages/sensorToMotor";
import { calculateMotorToSeals } from "./stages/motorToSeals";
import { calculateSealsToIntake } from "./stages/sealsToIntake";
import { calculateIntakeGasSeparation } from "./stages/intakeGasSeparation";
import { calculatePumpIntakeFluidProperties } from "./stages/pumpIntake";
import { calculateTubingAndDischarge } from "./stages/tubing";

export type { FluidPropertiesPipelineInputs } from "./types";

export const performFluidPropertiesCalculations = (
  // well properties
  basicData: BasicData,
  correlations: Correlations,
  wellTestFlowRates: FlowRates,
  desiredFlowRates: FlowRates,
  wellFlowingPressureAtDatumPSIA: number,
  datumPointTVD: number,
  bubblePointPressurePSIA: number,
  condensateApi: number | null,
  dewPointPressurePSIA: number,
  includeCasingFrictionLoss: boolean,
  entryRows: DeviationSurveyEntryRows,
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null,
  fluidPropertiesOverrides: FluidPropertiesOverrides,
  tdhTuningFactor: number,
  // equipment properties
  bottomOfEquipmentMDFt: number,
  bottomOfEquipmentTVDFt: number,
  // sensor
  bottomOfSensorMDFt: number,
  bottomOfSensorTVDFt: number,
  sensorLengthFt: number,
  sensorOuterDiameterInches: number,
  sensorCrossSectionFt2: number,
  // tubings
  bottomOfTubingMDFt: number,
  tubingsDepthTVDFt: number,
  topTubingOuterDiameterInches: number,
  bottomTubingOuterDiameterInches: number,
  topOfPumpsMDFt: number,
  topOfPumpsTVDFt: number,
  boltOnDischargeOuterDiameterInches: number,
  // pumps
  bottomOfPumpsTVDFt: number,
  bottomOfPumpsMDFt: number,
  primaryPumpOuterDiameterInches: number,
  // intake
  orderedIntakesInfo: IntakeInfo[],
  primaryIntakeOuterDiameterInches: number,
  primaryIntakePortSquareInches: number,
  bottomOfIntakesTVDFt: number,
  bottomOfIntakesMDFt: number,
  intakePackerInstalled: boolean,
  naturalGasSeparationOverrideType: Override_Options_Enum | null | undefined,
  intakeGasSeparationOverrideType: Override_Options_Enum | null | undefined,
  naturalOverrideGasSeparationPercent: number,
  intakeOverrideGasSeparationPercent: number,
  intakeCrossSectionFt2: number,
  // seals
  bottomOfSealsMDFt: number,
  bottomOfSealsTVDFt: number,
  sealsCrossSectionFt2: number,
  primarySealOuterDiameterInches: number,
  // motor
  bottomOfMotorMDFt: number,
  bottomOfMotorTVDFt: number,
  motorCrossSectionFt2: number,
  effectiveCasingInteriorDiameterAtMotorIn: number,
  effectiveCasingInteriorDiameterAtSealsIn: number,
  effectiveCasingInteriorDiameterAtIntakeIn: number,
  effectiveCasingInteriorDiameterAtSensorIn: number,
  // casing geometry
  topCasingInteriorDiameterIn: number,
  topCasingLengthFt: number,
  bottomCasingInteriorDiameterIn: number,
  bottomCasingLengthFt: number,
  // tubing
  tubingInternalDiameterAtPumpDischargeIn: number,
  // shroud
  shroudInteriorDiameterIn?: number,
  shroudOuterDiameterIn?: number,
  overrides?: Partial<ScenarioCalculations>
): FluidPropertiesPipelineResults => {
  const inputs: FluidPropertiesPipelineInputs = {
    basicData,
    correlations,
    wellTestFlowRates,
    desiredFlowRates,
    wellFlowingPressureAtDatumPSIA,
    datumPointTVD,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    includeCasingFrictionLoss,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    tdhTuningFactor,
    bottomOfEquipmentMDFt,
    bottomOfEquipmentTVDFt,
    bottomOfSensorMDFt,
    bottomOfSensorTVDFt,
    sensorLengthFt,
    sensorOuterDiameterInches,
    sensorCrossSectionFt2,
    bottomOfTubingMDFt,
    tubingsDepthTVDFt,
    topTubingOuterDiameterInches,
    bottomTubingOuterDiameterInches,
    topOfPumpsMDFt,
    topOfPumpsTVDFt,
    boltOnDischargeOuterDiameterInches,
    bottomOfPumpsTVDFt,
    bottomOfPumpsMDFt,
    primaryPumpOuterDiameterInches,
    orderedIntakesInfo,
    primaryIntakeOuterDiameterInches,
    primaryIntakePortSquareInches,
    bottomOfIntakesTVDFt,
    bottomOfIntakesMDFt,
    intakePackerInstalled,
    naturalGasSeparationOverrideType,
    intakeGasSeparationOverrideType,
    naturalOverrideGasSeparationPercent,
    intakeOverrideGasSeparationPercent,
    intakeCrossSectionFt2,
    bottomOfSealsMDFt,
    bottomOfSealsTVDFt,
    sealsCrossSectionFt2,
    primarySealOuterDiameterInches,
    bottomOfMotorMDFt,
    bottomOfMotorTVDFt,
    motorCrossSectionFt2,
    effectiveCasingInteriorDiameterAtMotorIn,
    effectiveCasingInteriorDiameterAtSealsIn,
    effectiveCasingInteriorDiameterAtIntakeIn,
    effectiveCasingInteriorDiameterAtSensorIn,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt,
    tubingInternalDiameterAtPumpDischargeIn,
    shroudInteriorDiameterIn,
    shroudOuterDiameterIn,
    overrides
  };

  // Determine if the sensor is present based on its length
  const hasSensor = sensorLengthFt > 0;

  // Set data needed for determining shroud usage for casing friction
  const perfsAboveBottomOfEquipment =
    basicData.topOfPerforationsMDFt <= bottomOfEquipmentMDFt;
  const hasShroudDimensions =
    (shroudInteriorDiameterIn ?? 0) > 0 && (shroudOuterDiameterIn ?? 0) > 0;
  const useShroudCirculationPath =
    hasShroudDimensions && perfsAboveBottomOfEquipment;

  const datum = calculateDatumAndPressureGradient(inputs);
  const {
    fluidPropertiesAtDatumContext,
    wellFlowingPressureAtPerfsPSIA,
    perfsAndDatumCasingFrictionPSIA
  } = datum;

  const perfsToBottomOfEquipment = calculatePerfsToBottomOfEquipment(inputs, {
    hasSensor,
    useShroudCirculationPath,
    casingInteriorDiameterAtPerfsIn: datum.casingInteriorDiameterAtPerfsIn,
    casingInteriorDiameterAtBottomOfEquipmentRawIn:
      datum.casingInteriorDiameterAtBottomOfEquipmentRawIn,
    wellFlowingPressureAtPerfsPSIA,
    getPressureAtReferenceTVDPSIA: datum.getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: 0
  });
  const {
    downholeWellPerfsTemperatureF,
    fluidPropertiesAtWellPerfsContext,
    bottomOfEquipmentCasingFrictionPSIA
  } = perfsToBottomOfEquipment;

  const sensorToMotor = calculateSensorToMotor(inputs, {
    hasSensor,
    getPressureAtReferenceTVDPSIA: datum.getPressureAtReferenceTVDPSIA,
    perfsToBottomOfEquipmentCasingFrictionPSIA:
      perfsToBottomOfEquipment.perfsToBottomOfEquipmentCasingFrictionPSIA,
    downholeBottomOfEquipmentTemperatureF:
      perfsToBottomOfEquipment.downholeBottomOfEquipmentTemperatureF,
    fluidPropertiesAtBottomOfEquipmentContext:
      perfsToBottomOfEquipment.fluidPropertiesAtBottomOfEquipmentContext,
    casingFrictionLossPSIA: perfsToBottomOfEquipment.casingFrictionLossPSIA
  });
  const {
    wellFlowingPressureAtSensorPSIA,
    downholeSensorTemperatureF,
    fluidPropertiesAtSensorContext,
    wellFlowingPressureAtMotorPSIA,
    downholeMotorTemperatureF,
    fluidPropertiesAtMotorContext,
    sensorAndMotorCasingFrictionPSIA
  } = sensorToMotor;

  const motorToSeals = calculateMotorToSeals(inputs, {
    fluidPropertiesAtMotorContext,
    getPressureAtReferenceTVDPSIA: datum.getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: sensorToMotor.casingFrictionLossPSIA
  });
  const {
    motorAndSealsCasingFrictionPSIA,
    wellFlowingPressureAtSealPSIA,
    downholeSealTemperatureF,
    fluidPropertiesAtSealsContext
  } = motorToSeals;

  const {
    sealsAndIntakesCasingFrictionPSIA,
    wellFlowingPressureAtIntakePSIA,
    downholeIntakeTemperatureF,
    casingFrictionLossPSIA
  } = calculateSealsToIntake(inputs, {
    fluidPropertiesAtSealsContext,
    getPressureAtReferenceTVDPSIA: datum.getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: motorToSeals.casingFrictionLossPSIA
  });

  const { gasSeparationEfficiencies, fluidPropertiesAtIntakeContext } =
    calculateIntakeGasSeparation(inputs, {
      wellFlowingPressureAtIntakePSIA,
      downholeIntakeTemperatureF,
      fluidPropertiesAtSealsContext
    });

  const {
    wellFlowingPressureAtPumpPSIA,
    downholePumpTemperatureF,
    fluidPropertiesAtPumpIntakeContext,
    fluidPropertiesAfterAllGasSeparationAtPumpContext
  } = calculatePumpIntakeFluidProperties(inputs, {
    wellFlowingPressureAtIntakePSIA,
    fluidPropertiesAtIntakeContext,
    gasSeparationEfficiencies
  });

  const {
    downholeTubingTemperatureF,
    fluidPropertiesInsideTubingContext,
    tubingFrictionLossPSIA,
    topTubingFrictionProperties,
    bottomTubingFrictionProperties,
    adjustedRequiredDischargePressurePSIA,
    fluidPropertiesAfterPumpDischargeTubingContext
  } = calculateTubingAndDischarge(inputs, {
    wellFlowingPressureAtPumpPSIA,
    fluidPropertiesAfterAllGasSeparationAtPumpContext
  });

  if (!fluidPropertiesAtIntakeContext.fluidProperties)
    throw new Error("fluidPropertiesAtIntakeContext invalid");
  if (!fluidPropertiesAtWellPerfsContext.fluidProperties)
    throw new Error("fluidPropertiesAtWellPerfsContext invalid");
  if (!fluidPropertiesAtSealsContext.fluidProperties)
    throw new Error("fluidPropertiesAtSealsContext invalid");
  if (!fluidPropertiesAtMotorContext.fluidProperties)
    throw new Error("fluidPropertiesAtMotorContext invalid");
  if (!fluidPropertiesAtPumpIntakeContext.fluidProperties)
    throw new Error("fluidPropertiesAtPumpIntakeContext invalid");
  if (hasSensor && !fluidPropertiesAtSensorContext?.fluidProperties)
    throw new Error("fluidPropertiesAtSensorContext invalid");

  return {
    fluidPropertiesCalculationContexts: {
      fluidPropertiesAtDatumContext,
      fluidPropertiesAtWellPerfsContext,
      fluidPropertiesAtSensorContext,
      fluidPropertiesAtMotorContext,
      fluidPropertiesAtSealsContext,
      fluidPropertiesAtIntakeContext,
      fluidPropertiesAtPumpIntakeContext,
      fluidPropertiesAfterAllGasSeparationAtPumpContext,
      fluidPropertiesInsideTubingContext,
      fluidPropertiesAfterPumpDischargeTubingContext
    },
    fluidPropertiesInputTemperatures: {
      downholeWellPerfsTemperatureF,
      downholeSensorTemperatureF,
      downholeMotorTemperatureF,
      downholeSealTemperatureF,
      downholeIntakeTemperatureF,
      downholePumpTemperatureF,
      downholeTubingTemperatureF
    },
    fluidPropertiesInputPressures: {
      wellFlowingPressureAtPerfsPSIA,
      wellFlowingPressureAtSensorPSIA,
      wellFlowingPressureAtMotorPSIA,
      wellFlowingPressureAtSealPSIA,
      wellFlowingPressureAtIntakePSIA,
      wellFlowingPressureAtPumpPSIA
    },
    gasSeparationEfficiencies,
    requiredDischargePressurePSIA: adjustedRequiredDischargePressurePSIA,
    tubingFrictionLossPSIA,
    topTubingFrictionProperties,
    bottomTubingFrictionProperties,
    casingFrictionLossPSIA,
    perfsAndDatumCasingFrictionPSIA,
    bottomOfEquipmentCasingFrictionPSIA,
    sensorAndMotorCasingFrictionPSIA,
    motorAndSealsCasingFrictionPSIA,
    sealsAndIntakesCasingFrictionPSIA
  };
};
